import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";
import { getConfig } from "@/config";

let manager: UserManager | undefined;

/**
 * The OIDC client, configured for Authorization Code + PKCE.
 *
 * The SPA is a *public* client: it holds no secret, because anything shipped to
 * a browser is readable by its user. PKCE is what makes that safe — an
 * intercepted authorization code is useless without the verifier.
 */
export function getUserManager(): UserManager {
  if (manager) return manager;

  const { oidc } = getConfig();

  manager = new UserManager({
    authority: oidc.authority,
    client_id: oidc.clientId,
    redirect_uri: oidc.redirectUri,
    post_logout_redirect_uri: oidc.postLogoutRedirectUri,
    scope: oidc.scope,
    response_type: "code",

    // Hydra issues an access token with `aud` only if the request names the
    // audience. Unset for providers that add it themselves.
    ...(oidc.audience ? { extraQueryParams: { audience: oidc.audience } } : {}),

    // The access token lives in memory only. sessionStorage keeps the flow
    // state (PKCE verifier, nonce) across the redirect, which it must survive,
    // but tokens are never written to storage a persistent XSS could read after
    // the tab closes.
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),

    // Renew silently ahead of expiry so a long session does not bounce the user
    // to the IdP mid-task.
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 60,

    // Strip the code and state from the URL after the exchange, so an
    // authorization code never lingers in history or a copied link.
    loadUserInfo: false,
  });

  return manager;
}

/** Starts a login redirect to the identity provider. */
export async function login(): Promise<void> {
  await getUserManager().signinRedirect();
}

let completion: Promise<User> | undefined;

/**
 * Completes the redirect back from the identity provider.
 *
 * Memoised because an authorization code is single-use: React StrictMode
 * double-invokes effects in development, and a second exchange of the same code
 * is correctly rejected by the provider. Sharing one in-flight promise makes the
 * call idempotent however many times it is invoked.
 */
export function completeLogin(): Promise<User> {
  completion ??= getUserManager().signinRedirectCallback();
  return completion;
}

/** Ends the session at the identity provider. */
export async function logout(): Promise<void> {
  await getUserManager().signoutRedirect();
}

/** Returns the stored user, or null when not signed in or expired. */
export async function getUser(): Promise<User | null> {
  const user = await getUserManager().getUser();
  if (!user || user.expired) return null;
  return user;
}

/** Resets the singletons. Tests only. */
export function resetUserManagerForTests(): void {
  manager = undefined;
  completion = undefined;
}
